import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Car, X, Upload } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 2.5 * 1024 * 1024; // 2.5MB raw; base64 ~33% larger ≈ under 3MB

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const emptyForm = {
  year: '',
  make: '',
  model: '',
  color: '',
  mods: '',
  photo_base64: ''
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AccountVehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/account/vehicles`, { headers: authHeaders() });
      setVehicles(res.data);
    } catch (err) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      year: v.year,
      make: v.make,
      model: v.model,
      color: v.color,
      mods: v.mods || '',
      photo_base64: v.photo_base64 || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Image must be under 2.5MB');
      return;
    }
    try {
      const dataUri = await fileToBase64(file);
      setForm((f) => ({ ...f, photo_base64: dataUri }));
    } catch {
      toast.error('Failed to read image');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.photo_base64 && !editing) {
      toast.error('Please upload a photo of your vehicle');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        year: parseInt(form.year, 10),
        make: form.make.trim(),
        model: form.model.trim(),
        color: form.color.trim(),
        mods: form.mods.trim() || null,
        photo_base64: form.photo_base64 || null
      };
      if (editing) {
        await axios.put(`${API_URL}/api/account/vehicles/${editing.id}`, payload, {
          headers: authHeaders()
        });
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API_URL}/api/account/vehicles`, payload, {
          headers: authHeaders()
        });
        toast.success('Vehicle added to your garage');
      }
      await fetchVehicles();
      closeForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this vehicle from your garage?')) return;
    try {
      await axios.delete(`${API_URL}/api/account/vehicles/${id}`, { headers: authHeaders() });
      toast.success('Vehicle removed');
      fetchVehicles();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="account-vehicles">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Oswald'] text-2xl text-[#F2EDE6] tracking-wider">MY GARAGE</h2>
        <button
          onClick={openAdd}
          data-testid="add-vehicle-btn"
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={16} /> Add Vehicle
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-[#E8651A] animate-spin mx-auto" />
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#2A2A2A]">
          <Car className="w-12 h-12 text-[#2A2A2A] mx-auto mb-3" />
          <p className="text-[#A19D98]">No vehicles yet. Add your ride to register for rallies.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {vehicles.map((v) => (
            <div
              key={v.id}
              data-testid={`vehicle-card-${v.id}`}
              className="bg-[#0A0A0A] border border-[#2A2A2A] overflow-hidden"
            >
              {v.photo_base64 ? (
                <img src={v.photo_base64} alt={`${v.year} ${v.make} ${v.model}`} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-[#141414] flex items-center justify-center">
                  <Car className="w-12 h-12 text-[#2A2A2A]" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-['Oswald'] text-[#F2EDE6] tracking-wider">
                  {v.year} {v.make.toUpperCase()} {v.model.toUpperCase()}
                </h3>
                <p className="text-[#A19D98] text-sm">{v.color}</p>
                {v.mods && <p className="text-[#A19D98] text-xs mt-2 italic">{v.mods}</p>}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => openEdit(v)}
                    data-testid={`vehicle-edit-${v.id}`}
                    className="text-[#A19D98] hover:text-[#E8651A] text-sm flex items-center gap-1"
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(v.id)}
                    data-testid={`vehicle-delete-${v.id}`}
                    className="text-[#A19D98] hover:text-red-400 text-sm flex items-center gap-1"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-[#141414] border border-[#2A2A2A] p-6 w-full max-w-lg my-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Oswald'] text-xl text-[#F2EDE6] tracking-wider">
                {editing ? 'EDIT VEHICLE' : 'ADD VEHICLE'}
              </h3>
              <button onClick={closeForm} className="text-[#A19D98] hover:text-[#F2EDE6]">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[#A19D98] text-xs mb-1">Year *</label>
                  <Input
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    required
                    data-testid="vehicle-year-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-xs mb-1">Make *</label>
                  <Input
                    value={form.make}
                    onChange={(e) => setForm({ ...form, make: e.target.value })}
                    required
                    data-testid="vehicle-make-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-xs mb-1">Model *</label>
                  <Input
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    required
                    data-testid="vehicle-model-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[#A19D98] text-xs mb-1">Color *</label>
                <Input
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  required
                  data-testid="vehicle-color-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>
              <div>
                <label className="block text-[#A19D98] text-xs mb-1">Mods / Changes (optional)</label>
                <Textarea
                  value={form.mods}
                  onChange={(e) => setForm({ ...form, mods: e.target.value })}
                  rows={2}
                  placeholder="e.g. coilovers, cat-back, wrap, etc."
                  data-testid="vehicle-mods-input"
                  className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                />
              </div>
              <div>
                <label className="block text-[#A19D98] text-xs mb-1">
                  Photo {!editing && '*'}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileRef}
                    onChange={handlePhotoChange}
                    data-testid="vehicle-photo-input"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="btn-secondary flex items-center gap-2 text-sm"
                  >
                    <Upload size={14} /> {form.photo_base64 ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {form.photo_base64 && (
                    <img src={form.photo_base64} alt="preview" className="w-16 h-16 object-cover border border-[#2A2A2A]" />
                  )}
                </div>
                <p className="text-[#A19D98] text-xs mt-1">Max 2.5MB</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="vehicle-save-btn"
                  className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save Vehicle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountVehicles;
